import { useTheme } from 'emotion-theming';
import React, { useEffect, useState } from 'react';
import { Box, Flex, Text } from 'rebass';
import { useHistory } from 'react-router-dom';
import ActivityFeedList from '../ActivityFeed/ActivityFeedList';
import { ACTIVITY_ITEMS_UPDATED_EVENT } from '../ActivityFeed/ActivityPollingToast';
import useActivityFeed from '../ActivityFeed/useActivityFeed';
import Icon from '../Icon/Icon';
import Spinner from '../Spinner/Spinner';
import { Theme } from '@/theme';

type SidebarActivityAccordionProps = {
  mode: string;
};

const ACTIVITY_ACCORDION_OPEN_KEY = 'sidebarActivityAccordionOpen';

const getStoredOpenState = (): boolean => {
  try {
    return localStorage.getItem(ACTIVITY_ACCORDION_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
};

const setStoredOpenState = (open: boolean) => {
  try {
    localStorage.setItem(ACTIVITY_ACCORDION_OPEN_KEY, String(open));
  } catch {
    // Ignore storage failures; the accordion can still work for this session.
  }
};

const SidebarActivityAccordion = ({
  mode,
}: SidebarActivityAccordionProps): JSX.Element => {
  const theme = useTheme<Theme>();
  const history = useHistory();
  const [open, setOpen] = useState(getStoredOpenState);
  const { data, items, loading, error, refetch, hasUnread, hasFollowUp } =
    useActivityFeed(15);

  useEffect(() => {
    const handleActivityItemsUpdated = () => {
      void refetch();
    };

    window.addEventListener(
      ACTIVITY_ITEMS_UPDATED_EVENT,
      handleActivityItemsUpdated
    );
    return () => {
      window.removeEventListener(
        ACTIVITY_ITEMS_UPDATED_EVENT,
        handleActivityItemsUpdated
      );
    };
  }, [refetch]);

  return (
    <Flex
      flexDirection="column"
      sx={{
        flex: '0 1 auto',
        minHeight: 0,
        maxHeight: 'min(45vh, 520px)',
        mt: 'auto',
        borderTop: `1px solid ${theme.colors.base.gray[200]}`,
        overflow: 'hidden',
        transition: 'max-height .22s ease',
      }}
    >
      <Flex
        role="button"
        tabIndex={0}
        alignItems="center"
        px={3}
        pt="10px"
        pb="10px"
        onClick={() =>
          setOpen((value) => {
            setStoredOpenState(!value);
            return !value;
          })
        }
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((value) => {
              setStoredOpenState(!value);
              return !value;
            });
          }
        }}
        sx={{
          gap: 2,
          flexShrink: 0,
          color: theme.colors.base.gray[500],
          fontSize: 1,
          fontWeight: 'heading',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          borderBottom: open
            ? `1px solid ${theme.colors.base.gray[200]}`
            : 'none',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.colors.base.gray[100],
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: theme.colors.primary,
            outlineOffset: '-2px',
          },
        }}
      >
        <Flex
          alignItems="center"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            gap: 2,
            color: 'inherit',
            textAlign: 'left',
          }}
        >
          <Box sx={{ position: 'relative', lineHeight: 0, flexShrink: 0 }}>
            <Icon icon="bell" size={16} />
            {(hasUnread || hasFollowUp) && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: -1,
                  right: -1,
                  width: 7,
                  height: 7,
                  borderRadius: 'circle',
                  bg: hasUnread ? 'primary' : 'statusOrange',
                  border: '1px solid',
                  borderColor: 'navbar',
                }}
              />
            )}
          </Box>
          <Text
            as="span"
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Activity
          </Text>
          <Icon
            icon={open ? 'chevron-down' : 'chevron-right'}
            size={14}
            style={{ flexShrink: 0 }}
          />
          {loading && (
            <Spinner
              size={12}
              color={theme.colors.primary}
              backgroundColor={theme.colors.base.gray[200]}
            />
          )}
        </Flex>
        <Box
          as="button"
          type="button"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            history.push(`/${mode}/activity`);
          }}
          onKeyDown={(event: React.KeyboardEvent) => {
            event.stopPropagation();
          }}
          sx={{
            flexShrink: 0,
            p: 0,
            border: 'none',
            bg: 'transparent',
            color: theme.colors.primary,
            cursor: 'pointer',
            fontSize: 0,
            lineHeight: 'interactive',
            textTransform: 'none',
            letterSpacing: 0,
            textDecoration: 'none',
            '&:hover, &:focus-visible': {
              textDecoration: 'underline',
            },
          }}
        >
          See all
        </Box>
      </Flex>
      <Box
        sx={{
          flex: open ? '1 1 auto' : '0 0 auto',
          minHeight: 0,
          maxHeight: open ? 'min(38vh, 456px)' : 0,
          opacity: open ? 1 : 0,
          bg: 'white',
          overflow: 'hidden',
          transition: 'max-height .22s ease, opacity .16s ease',
        }}
      >
        <Box
          sx={{
            maxHeight: 'inherit',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <ActivityFeedList
            items={items}
            loading={loading}
            error={error}
            hasData={!!data}
            emptyMessage="No notifications yet."
            variant="menu"
          />
        </Box>
      </Box>
    </Flex>
  );
};

export default SidebarActivityAccordion;
